import { useState } from 'react';
import { buildPrintDocumentTitle } from '../../lib/printDocumentTitle';
import {
  RESUME_TEMPLATE_META_BY_FAMILY,
  isBuiltFamily,
  type ResumeTemplateFamily,
} from '../../lib/resumeTemplateRegistry';
import { useResume } from '../../lib/useResume';
import { printResume } from '../../lib/resumeToPrint';
import { useTemplatePreference } from '../../lib/useTemplatePreference';
import { Card } from '../Card';
import { ExportFlow, ExportTriggerButton } from './ExportFlow';
import { ResumeView } from './ResumeView';
import styles from './JobResumePanel.module.css';

/** The saved family's own displayName from the shared registry meta — see
 *  CorpusResumePanel.tsx's identical helper for the full rationale (single
 *  source of truth, no second name list). */
function currentFamilyLabel(family: ResumeTemplateFamily): string {
  return RESUME_TEMPLATE_META_BY_FAMILY[family]?.displayName ?? family;
}

const FORMAT_LABEL: Record<string, string> = {
  au_standard: 'AU standard',
  au_selection_criteria: 'AU selection criteria',
  us_standard: 'US standard',
  gb_standard: 'GB standard',
};

// =============================================================================
// JobResumePanel (throughline) — GAP 1, the biggest wiring hole a read-only
// audit found in the daily loop: POST /jobs/:id/resume (jobs.routes.ts:1392,
// fully built, deterministic, NOT entitlement-gated) has never been called
// from this app. Ported from apps/web/src/components/resume/ResumePanel.tsx
// (the Build/Rebuild button + linter chips + live preview) crossed with this
// app's own already-ported CorpusResumePanel.tsx (the honest empty/loading
// states + the REAL printResume Print/Save-as-PDF affordance, same pattern,
// no re-derivation).
//
// THIN PRESENTATION / NO INVENTION — every field rendered here is the
// server's own composed ResumeDocument (ResumeComposerService); this panel
// never orders, summarises, or invents any of it. `scenarioId` is passed
// through from JobDetail's own resolved scenario (mirrors the tailor call's
// own scenario resolution) so the résumé is built under the SAME lens the
// user is already tailoring against — never a second, disagreeing scenario
// guess.
//
// Mounted on JobDetail AFTER the tailor result card (per the brief's flow):
// once a tailor has run OR a cvBrief already exists for this job, the user
// can build the tailored résumé DOCUMENT for this specific job, view it, and
// Print/Save as PDF — closing the "tailored a letter but never got the CV"
// gap. Before that point this panel does not render at all (JobDetail decides
// whether to mount it — see this app's own house rule: a component renders
// its own honest states, but a page decides whether it's relevant to show).
//
// GAP 2 (SMALL-WAVE completeness pass) — Download .docx, ported from
// apps/web/src/components/resume/ResumePanel.tsx:22-30/102-110. The
// mechanism (lib/resumeToDocx.ts) is entirely client-side — no server
// route to add; this panel just triggers the SAME browser download flow
// apps/web's ResumePanel already offers, so a phone user hitting an ATS
// portal that wants an editable .docx (not everyone accepts a printed PDF)
// isn't stuck without one. Filename via the SAME buildPrintDocumentTitle
// convention the Print button already uses (artifact: 'Resume') — one
// house naming rule for both artifacts of this same document.
// =============================================================================

export function JobResumePanel({
  jobId,
  scenarioId,
  roleTitle,
  company,
  applicantName,
}: {
  jobId: string;
  scenarioId?: string;
  /** The served job title/company (JobDetail's own job.title/job.company) —
   *  used ONLY to name the print popup per the house file-naming convention
   *  (printDocumentTitle.ts); never rendered into the résumé document itself
   *  (that content is entirely server-composed). Optional/omittable: an
   *  absent value simply drops that segment from the filename, never a
   *  placeholder. */
  roleTitle?: string | null;
  company?: string | null;
  /** The signed-in user's real displayName (useAuth), passed down from
   *  JobDetail rather than this panel calling useAuth() a second time —
   *  same real, served value Home.tsx/Settings.tsx already render. */
  applicantName?: string | null;
}) {
  const { current, versions, building, error, build } = useResume(jobId);
  const [printNote, setPrintNote] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  // TEMPLATES-P2 (§7/§9/§10) — the persisted family + accent, still read
  // here for the current-template indicator + ExportFlow's pre-selection.
  //
  // EXPORT-FLOW (2026-07-06, docs/design/export-workflow-2026-07-06.md) —
  // the owner's verbatim ruling: "We don't need to see the resume templates
  // until we want to export, which should be a different workflow." See
  // CorpusResumePanel.tsx's identical header note — the Download PDF/.docx/
  // Save-to-Drive buttons and the inline TemplatePicker that used to live
  // here now live ONLY inside ExportFlow, opened by the single Export
  // trigger below.
  const { family, accentKey, setFamily, setAccent } = useTemplatePreference();

  const doc = current?.document ?? null;
  const lr = current?.linterReport ?? null;
  const blocked = current?.blocked === true;

  function handlePrint() {
    const title = buildPrintDocumentTitle({
      displayName: applicantName,
      artifact: 'Resume',
      roleTitle,
      company,
    });
    const ok = printResume(title);
    setPrintNote(ok ? null : 'Couldn’t open the print window — allow pop-ups for this site and try again.');
  }

  return (
    <Card className={styles.section}>
      <div className={styles.headerRow}>
        <div className={styles.sectionTitle}>
          Résumé for this role
          {versions.length > 0 ? (
            <span className={styles.versionChip}>
              {versions.length} version{versions.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.buildButton}
          onClick={() => build({ scenarioId })}
          disabled={building}
        >
          {building ? 'Building…' : doc ? 'Rebuild résumé' : 'Build résumé'}
        </button>
      </div>

      {error ? (
        <div className={styles.errorNote} role="alert">
          Couldn’t build that: {error}
        </div>
      ) : null}

      {/* FIX 1 (owner's live walk, 2026-07-05 — collapse every informational
          blurb by default, keep the ACTION control — the Build résumé button
          above — visible). This static descriptive line is presentation-only
          (no generation logic touched); reuses the SAME native <details>/
          <summary className={styles.sectionTitle}> idiom JobDetail.tsx's own
          collapsible cards establish, closed by default. */}
      {!doc && !building && !error ? (
        <details>
          <summary className={styles.sectionTitle} style={{ cursor: 'pointer' }}>
            What this does
          </summary>
          <p className={styles.emptyNote} style={{ marginTop: 8 }}>
            Composes a résumé from your story, tailored to this role and ordered by what you
            just tailored above. Nothing invented — every line traces to your story.
          </p>
        </details>
      ) : null}

      {building ? <div className={styles.loadingNote}>Composing your résumé…</div> : null}

      {doc ? (
        <>
          <div className={styles.chipRow}>
            <span className={styles.formatChip}>
              {FORMAT_LABEL[doc.formatVariant] ?? doc.formatVariant}
            </span>
            <span className={styles.scenarioChip}>{current?.scenarioName}</span>
            <LinterChip
              ok={!!lr?.passing}
              label={
                lr?.passing
                  ? 'Readability clean'
                  : `${lr?.findings.length ?? 0} block${(lr?.findings.length ?? 0) === 1 ? '' : 's'} over budget`
              }
            />
          </div>

          {blocked ? (
            <div className={styles.blockedNote} role="alert">
              I held this back — a line in the draft needed another pass before I’d show it as
              ready. Try building again; it often resolves on a retry.
            </div>
          ) : (
            <>
              <div className={styles.actionsRow}>
                <ExportTriggerButton
                  onClick={() => setExportOpen(true)}
                  open={exportOpen}
                  className={styles.printButton}
                />
                <button type="button" className={styles.printButton} onClick={handlePrint}>
                  Print from browser
                </button>
              </div>
              {printNote ? <div className={styles.toolbarNote}>{printNote}</div> : null}

              {lr && !lr.passing ? (
                <p className={styles.toolbarNote}>
                  The over-budget blocks are your own verbatim wording — technical phrasing
                  reads dense to the readability score. Nothing is auto-rewritten.
                </p>
              ) : null}

              {/* EXPORT-FLOW — the current template as a plain line, not a
                  control (the owner's ruling: templates belong to the export
                  workflow, not the reading flow). */}
              {isBuiltFamily(family) ? (
                <div className={styles.toolbarNote}>Current look: {currentFamilyLabel(family)}</div>
              ) : null}

              <ExportFlow
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                doc={doc}
                family={family}
                accentKey={accentKey}
                onSelectFamily={setFamily}
                onSelectAccent={setAccent}
                artifactId={current?.resumeId ?? null}
                artifactType="resume"
                displayName={applicantName}
                roleTitle={roleTitle}
                company={company}
                artifactLabel="Resume"
              />

              <div className={styles.previewFrame}>
                <div className={styles.previewScale}>
                  <ResumeView doc={doc} family={isBuiltFamily(family) ? family : undefined} accentKey={accentKey} />
                </div>
              </div>
            </>
          )}
        </>
      ) : null}
    </Card>
  );
}

function LinterChip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={ok ? styles.lintChipGood : styles.lintChipWarm}>{label}</span>
  );
}
